/**
 * The virtual screen every scene draws into (docs/spec/04 §1): 320 x 200 for the game and menus,
 * wider in Wide view, 640 x 360 for the Construction Kit. The display pass scales it to the window.
 */

import { context2d, makeCanvas } from './canvas';
import type { Colour } from './font';
import { css, drawText, GLYPH, textWidth } from './font';

export type Align = 'left' | 'center' | 'right';

export class Screen {
  readonly canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;

  constructor(width = 320, height = 200) {
    this.canvas = makeCanvas(width, height);
    this.ctx = context2d(this.canvas);
  }

  get width(): number {
    return this.canvas.width;
  }

  get height(): number {
    return this.canvas.height;
  }

  /** Change the virtual size (clears it). */
  resize(width: number, height: number): void {
    if (width === this.canvas.width && height === this.canvas.height) return;
    this.canvas.width = width;
    this.canvas.height = height;
    this.ctx = context2d(this.canvas);
  }

  clear(colour: Colour = 0): void {
    this.fill(0, 0, this.width, this.height, colour);
  }

  fill(x: number, y: number, w: number, h: number, colour: Colour): void {
    this.ctx.fillStyle = css(colour);
    this.ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
  }

  /** A one-pixel outline inside the rectangle. */
  box(x: number, y: number, w: number, h: number, colour: Colour): void {
    this.fill(x, y, w, 1, colour);
    this.fill(x, y + h - 1, w, 1, colour);
    this.fill(x, y, 1, h, colour);
    this.fill(x + w - 1, y, 1, h, colour);
  }

  /** Text at (x, y); `align` is about x. */
  text(s: string, x: number, y: number, colour: Colour, align: Align = 'left', scale = 1): void {
    const w = textWidth(s, scale);
    const left = align === 'center' ? x - w / 2 : align === 'right' ? x - w : x;
    drawText(this.ctx, s, Math.round(left), y, colour, scale);
  }

  /** Text with a one-pixel drop shadow, for text over busy backgrounds. */
  shadowText(s: string, x: number, y: number, colour: Colour, shadow: Colour = 0, align: Align = 'left', scale = 1): void {
    this.text(s, x + scale, y + scale, shadow, align, scale);
    this.text(s, x, y, colour, align, scale);
  }

  /** Text on the 40-column character grid, as the C64 would print it. */
  print(s: string, col: number, row: number, colour: Colour): void {
    drawText(this.ctx, s, col * GLYPH, row * GLYPH, colour);
  }

  image(img: CanvasImageSource, x: number, y: number): void {
    this.ctx.drawImage(img, Math.round(x), Math.round(y));
  }
}

export { textWidth, GLYPH };
